import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, AlertTriangle, HelpCircle, ArrowRight, Receipt as ReceiptIcon, RefreshCw, Info, Camera, Loader2, X, Clock, ArrowLeftRight, ChevronDown, FileCheck, Archive, Search, SlidersHorizontal, Calendar } from "lucide-react";
import api from "@/lib/api";

const STATUS_LABEL = {
    found: { label: "Justificatif trouvé", color: "#34C759", icon: CheckCircle2 },
    manual_review: { label: "À valider", color: "#FF9F0A", icon: HelpCircle },
    missing: { label: "Manquant", color: "#FF3B30", icon: AlertTriangle },
    pending: { label: "Analyse…", color: "#86868B", icon: Clock },
    internal_transfer: { label: "Virement interne", color: "#0A84FF", icon: ArrowLeftRight },
};

function fmtMoney(v, cur = "EUR") {
    if (v == null) return "—";
    try { return new Intl.NumberFormat("fr-FR", { style: "currency", currency: cur.toUpperCase() }).format(v); }
    catch { return `${v} ${cur}`; }
}

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso; }
}

function StatusPill({ status, onExplain, onReclassify, txId }) {
    const cfg = STATUS_LABEL[status] || STATUS_LABEL.pending;
    const Icon = cfg.icon;
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const esc = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", handler);
        document.addEventListener("keydown", esc);
        return () => {
            document.removeEventListener("mousedown", handler);
            document.removeEventListener("keydown", esc);
        };
    }, [open]);

    const items = [
        { id: "explain", label: "Pourquoi ce statut ?", icon: Info, accent: "#1D1D1F", show: true, action: () => { setOpen(false); onExplain?.(); } },
        { id: "internal_transfer", label: "Marquer comme virement interne", desc: "Entre vos comptes ou à un proche", icon: ArrowLeftRight, accent: "#0A84FF", show: status !== "internal_transfer", action: () => { setOpen(false); onReclassify?.("internal_transfer"); } },
        { id: "found", label: "Marquer comme justifié", desc: "Justificatif conservé hors de l'app", icon: FileCheck, accent: "#34C759", show: status !== "found", action: () => { setOpen(false); onReclassify?.("found"); } },
        { id: "missing", label: "Remettre dans « Manquantes »", desc: "Réactiver la recherche de reçu", icon: Archive, accent: "#86868B", show: status !== "missing", action: () => { setOpen(false); onReclassify?.("missing"); } },
    ].filter((it) => it.show);

    return (
        <div ref={wrapRef} className="relative">
            <button
                onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition hover:opacity-80 hover:scale-[1.03] active:scale-95"
                style={{ color: cfg.color, backgroundColor: `${cfg.color}15` }}
                data-testid={`tx-status-${status}-${txId}`}
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <Icon className="w-3.5 h-3.5" /> {cfg.label}
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-72 bg-white rounded-2xl tk-shadow-float border border-black/5 overflow-hidden z-50 tk-menu-pop origin-top-right"
                    role="menu"
                    data-testid={`tx-status-menu-${txId}`}
                >
                    <div className="px-3 py-2 border-b border-black/5">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#86868B]">Reclasser cette transaction</p>
                    </div>
                    <div className="py-1">
                        {items.map((it) => {
                            const ItIcon = it.icon;
                            return (
                                <button
                                    key={it.id}
                                    onClick={it.action}
                                    className="w-full px-3 py-2.5 flex items-start gap-3 hover:bg-[#F5F5F7] active:bg-[#E5E5EA] transition text-left"
                                    role="menuitem"
                                    data-testid={`tx-reclass-${it.id}-${txId}`}
                                >
                                    <div
                                        className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                                        style={{ background: `${it.accent}12`, color: it.accent }}
                                    >
                                        <ItIcon className="w-4 h-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-[#1D1D1F] leading-tight">{it.label}</p>
                                        {it.desc && (
                                            <p className="text-[11px] text-[#86868B] mt-0.5 leading-snug">{it.desc}</p>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

function ScoreBar({ score, status }) {
    const raw = score?.score ?? 0;
    // Pin the bar to 100% when:
    //  - receipt is justified (found) — matching engine returns 0.78–0.95 on
    //    perfect matches which is technically accurate but visually confusing.
    //  - status is internal_transfer — no justificatif required, treat as done.
    const isComplete = status === "found" || status === "internal_transfer";
    const value = isComplete ? 1 : raw;
    const pct = Math.round(value * 100);
    let color = "#FF3B30";
    if (value >= 0.78) color = "#34C759";
    else if (value >= 0.55) color = "#FF9F0A";
    return (
        <div className="flex items-center gap-2 min-w-[110px]">
            <div className="flex-1 h-1.5 bg-[#F5F5F7] rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
            </div>
            <span className="text-xs font-semibold text-[#86868B] tabular-nums">{pct}%</span>
        </div>
    );
}

function ExplainModal({ open, onClose, data }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="explain-modal">
            <div className="absolute inset-0 backdrop-blur-2xl bg-black/20" onClick={onClose} />
            <div className="relative bg-white rounded-3xl tk-shadow-float w-full max-w-md p-7 tk-rise">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#F5F5F7] hover:bg-[#E5E5EA] flex items-center justify-center"
                    data-testid="explain-close"
                >
                    <X className="w-4 h-4" />
                </button>
                <div className="flex items-start gap-3 mb-4">
                    <Info className="w-5 h-5 text-[#007AFF] flex-shrink-0 mt-0.5" />
                    <h3 className="text-lg font-semibold text-[#1D1D1F]">{data?.title || "Pourquoi ce statut ?"}</h3>
                </div>
                <p className="text-sm text-[#86868B] leading-relaxed">{data?.body}</p>
                {data?.suggested_action === "wait" && (
                    <p className="mt-4 text-xs text-[#86868B]">
                        💡 Astuce : si vous attendez depuis longtemps, vérifiez vos spams ou re-synchronisez votre boîte mail dans Connexions.
                    </p>
                )}
            </div>
        </div>
    );
}

const FILTERS = [
    { id: "all", label: "Toutes" },
    { id: "found", label: "Justifiées" },
    { id: "manual_review", label: "À valider" },
    { id: "missing", label: "Manquantes" },
];

export default function Transactions() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [explain, setExplain] = useState(null);
    const [attachBusy, setAttachBusy] = useState(null);
    const attachInputRef = useRef(null);
    const attachTargetRef = useRef(null);

    // Search & advanced filters
    const [query, setQuery] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [amountMin, setAmountMin] = useState("");
    const [amountMax, setAmountMax] = useState("");
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [showInternal, setShowInternal] = useState(false);

    const refresh = useCallback(async (f = filter) => {
        setLoading(true);
        try {
            const { data } = await api.get("/transactions", { params: { status: f, include_internal: showInternal } });
            setItems(data);
        } finally {
            setLoading(false);
        }
    }, [filter, showInternal]);

    useEffect(() => { refresh(); }, [refresh]);

    // Client-side filtering (search, date, amount)
    const filteredItems = useMemo(() => {
        const q = query.trim().toLowerCase();
        const fromTs = dateFrom ? new Date(dateFrom + "T00:00:00").getTime() : null;
        const toTs = dateTo ? new Date(dateTo + "T23:59:59").getTime() : null;
        const minA = amountMin !== "" ? parseFloat(amountMin) : null;
        const maxA = amountMax !== "" ? parseFloat(amountMax) : null;
        return items.filter((t) => {
            if (q) {
                const m = (t.merchant_raw || "").toLowerCase();
                const id = (t.transaction_id || "").toLowerCase();
                if (!m.includes(q) && !id.includes(q)) return false;
            }
            if (fromTs || toTs) {
                const ts = t.transaction_date ? new Date(t.transaction_date).getTime() : null;
                if (!ts) return false;
                if (fromTs && ts < fromTs) return false;
                if (toTs && ts > toTs) return false;
            }
            const amt = Math.abs(parseFloat(t.amount) || 0);
            if (minA != null && !Number.isNaN(minA) && amt < minA) return false;
            if (maxA != null && !Number.isNaN(maxA) && amt > maxA) return false;
            return true;
        });
    }, [items, query, dateFrom, dateTo, amountMin, amountMax]);

    const activeFilterCount = (dateFrom ? 1 : 0) + (dateTo ? 1 : 0) + (amountMin !== "" ? 1 : 0) + (amountMax !== "" ? 1 : 0);

    const resetSearch = () => {
        setQuery("");
        setDateFrom("");
        setDateTo("");
        setAmountMin("");
        setAmountMax("");
    };

    const showExplain = async (txId) => {
        try {
            const { data } = await api.get(`/transactions/${txId}/explain`);
            setExplain(data);
        } catch {
            toast.error("Impossible de charger l'explication");
        }
    };

    const triggerAttach = (txId) => {
        attachTargetRef.current = txId;
        attachInputRef.current?.click();
    };

    const handleAttachFile = async (file) => {
        if (!file || !attachTargetRef.current) return;
        const txId = attachTargetRef.current;
        attachTargetRef.current = null;
        setAttachBusy(txId);
        const tid = toast.loading("Analyse du ticket en cours…");
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/transactions/${txId}/attach-receipt`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Justificatif attaché à la transaction", { id: tid });
            refresh();
        } catch (e) {
            if (e?.response?.status === 402) {
                toast.error(e?.response?.data?.detail || "Limite du plan atteinte", {
                    id: tid,
                    duration: 10000,
                    action: { label: "Passer au Pro", onClick: () => navigate("/app/forfait") },
                });
            } else {
                toast.error(e?.response?.data?.detail || "Échec", { id: tid });
            }
        } finally {
            setAttachBusy(null);
        }
    };

    const handleRematch = async (id) => {
        const tid = toast.loading("Rematching…");
        try {
            await api.post(`/transactions/${id}/rematch`);
            toast.success("Réanalyse terminée", { id: tid });
            refresh();
        } catch {
            toast.error("Échec", { id: tid });
        }
    };

    const handleReclassify = async (txId, newStatus) => {
        const labels = {
            internal_transfer: "Virement interne",
            found: "Justifié hors-app",
            missing: "Manquant",
        };
        const tid = toast.loading("Mise à jour…");
        try {
            await api.patch(`/transactions/${txId}/status`, { status: newStatus });
            toast.success(`Reclassée comme « ${labels[newStatus]} »`, { id: tid });
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Échec", { id: tid });
        }
    };

    const handleRematchAll = async () => {
        const tid = toast.loading("Réanalyse de toutes les transactions…");
        try {
            const { data } = await api.post("/transactions/rematch-all");
            toast.success(
                `${data.processed} analysée${data.processed > 1 ? "s" : ""} · ${data.newly_matched} nouveau${data.newly_matched > 1 ? "x" : ""} justificatif${data.newly_matched > 1 ? "s" : ""} trouvé${data.newly_matched > 1 ? "s" : ""}`,
                { id: tid, duration: 5000 },
            );
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Échec", { id: tid });
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-6 sm:px-8 space-y-8">
            <div className="tk-rise">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">Carte pro</p>
                <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">Transactions</h1>
                <p className="mt-3 text-[#86868B] max-w-xl">
                    Chaque paiement est analysé et associé à son justificatif. Cliquez le statut pour comprendre.
                </p>
            </div>

            <input
                ref={attachInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,application/pdf,.pdf"
                capture="environment"
                className="hidden"
                onChange={(e) => handleAttachFile(e.target.files?.[0])}
                data-testid="attach-input"
            />

            <div className="tk-rise tk-rise-1 flex flex-wrap items-center gap-2">
                {FILTERS.map((f) => (
                    <button
                        key={f.id}
                        onClick={() => setFilter(f.id)}
                        data-testid={`tx-filter-${f.id}`}
                        className={`px-4 py-2 rounded-full text-sm transition ${
                            filter === f.id ? "bg-[#1D1D1F] text-white" : "bg-white text-[#1D1D1F] border border-black/5 hover:bg-[#F5F5F7]"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}

                <div className="flex-1" />

                {/* Internal-transfer toggle */}
                <button
                    type="button"
                    onClick={() => setShowInternal((v) => !v)}
                    data-testid="tx-toggle-internal"
                    className={`px-3 py-2 rounded-full text-xs font-semibold transition ${
                        showInternal
                            ? "bg-[#86868B]/15 text-[#1D1D1F]"
                            : "bg-white text-[#86868B] border border-black/5 hover:bg-[#F5F5F7]"
                    }`}
                    title="Afficher / masquer les virements internes (P2P, Revolut → contact, transferts entre comptes)"
                >
                    {showInternal ? "Masquer virements internes" : "Voir virements internes"}
                </button>

                {/* Search bar */}
                <div className="relative w-full sm:w-auto sm:min-w-[260px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B] pointer-events-none" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Rechercher un marchand…"
                        className="w-full pl-9 pr-9 py-2 rounded-full bg-white border border-black/10 text-sm placeholder:text-[#86868B] focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition"
                        data-testid="tx-search-input"
                    />
                    {query && (
                        <button
                            onClick={() => setQuery("")}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-[#F5F5F7] flex items-center justify-center text-[#86868B]"
                            aria-label="Effacer"
                            data-testid="tx-search-clear"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>

                <button
                    onClick={() => setAdvancedOpen((v) => !v)}
                    className={`relative inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition border ${
                        advancedOpen || activeFilterCount > 0
                            ? "bg-[#007AFF]/10 text-[#007AFF] border-[#007AFF]/20"
                            : "bg-white text-[#1D1D1F] border-black/10 hover:bg-[#F5F5F7]"
                    }`}
                    data-testid="tx-advanced-toggle"
                    aria-expanded={advancedOpen}
                >
                    <SlidersHorizontal className="w-4 h-4" />
                    Filtres
                    {activeFilterCount > 0 && (
                        <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-[#007AFF] text-white">
                            {activeFilterCount}
                        </span>
                    )}
                </button>

                <button
                    onClick={handleRematchAll}
                    title="Relancer le matching sur toutes les transactions sans justificatif"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition bg-white text-[#1D1D1F] border border-black/10 hover:bg-[#F5F5F7]"
                    data-testid="tx-rematch-all"
                >
                    <RefreshCw className="w-4 h-4" />
                    <span className="hidden sm:inline">Réanalyser tout</span>
                </button>
            </div>

            {/* Advanced filter panel */}
            {advancedOpen && (
                <div className="tk-card p-5 tk-menu-pop origin-top" data-testid="tx-advanced-panel">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-[#86868B] mb-1.5 flex items-center gap-1.5">
                                <Calendar className="w-3 h-3" /> Du
                            </label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                max={dateTo || undefined}
                                className="tk-input"
                                data-testid="tx-filter-date-from"
                            />
                        </div>
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-[#86868B] mb-1.5 flex items-center gap-1.5">
                                <Calendar className="w-3 h-3" /> Au
                            </label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                min={dateFrom || undefined}
                                className="tk-input"
                                data-testid="tx-filter-date-to"
                            />
                        </div>
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-[#86868B] mb-1.5 block">
                                Montant min (€)
                            </label>
                            <input
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0"
                                value={amountMin}
                                onChange={(e) => setAmountMin(e.target.value)}
                                placeholder="0,00"
                                className="tk-input"
                                data-testid="tx-filter-amount-min"
                            />
                        </div>
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-[#86868B] mb-1.5 block">
                                Montant max (€)
                            </label>
                            <input
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0"
                                value={amountMax}
                                onChange={(e) => setAmountMax(e.target.value)}
                                placeholder="1 000,00"
                                className="tk-input"
                                data-testid="tx-filter-amount-max"
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-black/5">
                        <p className="text-xs text-[#86868B]">
                            {filteredItems.length} résultat{filteredItems.length > 1 ? "s" : ""} sur {items.length}
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={resetSearch}
                                disabled={!query && activeFilterCount === 0}
                                className="tk-btn tk-btn-secondary text-xs disabled:opacity-40"
                                data-testid="tx-filter-reset"
                            >
                                Réinitialiser
                            </button>
                            <button
                                onClick={() => setAdvancedOpen(false)}
                                className="tk-btn tk-btn-primary text-xs"
                                data-testid="tx-filter-apply"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <p className="text-[#86868B]">Chargement…</p>
            ) : items.length === 0 ? (
                <div className="tk-card p-12 text-center" data-testid="tx-empty">
                    <CreditCard className="w-12 h-12 text-[#AEAEB2] mx-auto mb-4" />
                    <p className="text-[#86868B] mb-4">Aucune transaction. Importez votre relevé bancaire dans Connexions.</p>
                    <Link to="/app/connections" className="tk-btn tk-btn-primary inline-flex">
                        Aller à Connexions <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            ) : filteredItems.length === 0 ? (
                <div className="tk-card p-12 text-center" data-testid="tx-no-results">
                    <Search className="w-10 h-10 text-[#AEAEB2] mx-auto mb-4" />
                    <p className="text-[#1D1D1F] font-medium mb-1">Aucun résultat</p>
                    <p className="text-[#86868B] text-sm mb-4">Aucune transaction ne correspond à votre recherche.</p>
                    <button onClick={resetSearch} className="tk-btn tk-btn-secondary inline-flex" data-testid="tx-no-results-reset">
                        Réinitialiser les filtres
                    </button>
                </div>
            ) : (
                <div className="tk-card divide-y divide-black/5 tk-rise tk-rise-2 [&>*:first-child]:rounded-t-3xl [&>*:last-child]:rounded-b-3xl">
                    {filteredItems.map((t) => {
                        const isInternal = t.is_internal_transfer || t.justificatif_status === "internal_transfer";
                        return (
                        <div
                            key={t.transaction_id}
                            className={`p-5 flex flex-wrap items-center gap-4 relative ${isInternal ? "bg-[#F5F5F7]/40 opacity-70" : ""}`}
                            data-testid={`tx-row-${t.transaction_id}`}
                        >
                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isInternal ? "bg-[#86868B]/15" : "bg-[#F5F5F7]"}`}>
                                <CreditCard className={`w-5 h-5 ${isInternal ? "text-[#86868B]" : "text-[#1D1D1F]"}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={`font-medium truncate ${isInternal ? "text-[#86868B]" : "text-[#1D1D1F]"}`}>{t.merchant_raw}</p>
                                <p className="text-xs text-[#86868B]">
                                    {fmtDate(t.transaction_date)} · {t.card_label}{t.card_last4 ? ` ••${t.card_last4}` : ""}
                                </p>
                            </div>

                            <div className="text-right min-w-[120px]">
                                <p className={`font-semibold ${isInternal ? "text-[#86868B]" : "text-[#1D1D1F]"}`}>
                                    {fmtMoney(t.amount, t.currency)}
                                    {!isInternal && t.fx_hint && (
                                        <Link
                                            to={`/app/receipt/${t.fx_hint.receipt_id}`}
                                            title={`Reçu trouvé en ${t.fx_hint.original_currency} — cliquez pour voir`}
                                            className="ml-1.5 text-[11px] font-normal text-[#007AFF] tabular-nums hover:underline"
                                            data-testid={`tx-fx-hint-${t.transaction_id}`}
                                        >
                                            ({fmtMoney(t.fx_hint.original_amount, t.fx_hint.original_currency)})
                                        </Link>
                                    )}
                                </p>
                            </div>

                            {isInternal ? (
                                <span
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#86868B]/15 text-[#86868B] text-[11px] font-semibold"
                                    data-testid={`tx-internal-${t.transaction_id}`}
                                    title="Virement interne / P2P — aucun justificatif requis"
                                >
                                    <RefreshCw className="w-3 h-3" />
                                    Virement interne · non requis
                                </span>
                            ) : (
                                <>
                                    <ScoreBar score={t.match_score} status={t.justificatif_status} />
                                    <StatusPill
                                        status={t.justificatif_status}
                                        txId={t.transaction_id}
                                        onExplain={() => showExplain(t.transaction_id)}
                                        onReclassify={(newStatus) => handleReclassify(t.transaction_id, newStatus)}
                                    />
                                </>
                            )}

                            <div className="flex items-center gap-2">
                                {t.matched_receipt ? (
                                    <Link
                                        to={`/app/receipt/${t.matched_receipt.receipt_id}`}
                                        className="tk-btn tk-btn-secondary text-xs px-3 py-1.5"
                                        data-testid={`tx-view-receipt-${t.transaction_id}`}
                                    >
                                        <ReceiptIcon className="w-3.5 h-3.5" /> Voir
                                    </Link>
                                ) : t.justificatif_status === "internal_transfer" ? (
                                    <span className="text-xs text-[#86868B] italic px-3">
                                        Aucun justificatif requis
                                    </span>
                                ) : t.justificatif_status === "missing" ? (
                                    <button
                                        onClick={() => triggerAttach(t.transaction_id)}
                                        disabled={attachBusy === t.transaction_id}
                                        className="tk-btn tk-btn-blue text-xs px-3 py-1.5"
                                        data-testid={`tx-scan-${t.transaction_id}`}
                                    >
                                        {attachBusy === t.transaction_id
                                            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyse</>
                                            : <><Camera className="w-3.5 h-3.5" /> Scanner</>
                                        }
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => handleRematch(t.transaction_id)}
                                        className="tk-btn tk-btn-secondary text-xs px-3 py-1.5"
                                        data-testid={`tx-rematch-${t.transaction_id}`}
                                    >
                                        <RefreshCw className="w-3.5 h-3.5" /> Réanalyser
                                    </button>
                                )}
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}

            <ExplainModal open={!!explain} onClose={() => setExplain(null)} data={explain} />
        </div>
    );
}
